import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import API from '../utils/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  const processQueue = (error) => {
    failedQueue.current.forEach(p => error ? p.reject(error) : p.resolve());
    failedQueue.current = [];
  };

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Skip refresh for login/logout/refresh endpoints, already retried, or non-401 errors
        const authEndpoints = ['/auth/login', '/auth/logout', '/auth/refresh', '/auth/me'];
        const isAuthEndpoint = authEndpoints.some(ep => originalRequest.url?.includes(ep));
        
        if (!error.response || error.response.status !== 401 || originalRequest._retry || isAuthEndpoint) {
          return Promise.reject(error);
        }

        if (isRefreshing.current) {
          // Queue requests while refresh is in progress
          return new Promise((resolve, reject) => {
            failedQueue.current.push({ resolve, reject });
          }).then(() => axios(originalRequest));
        }

        originalRequest._retry = true;
        isRefreshing.current = true;

        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          processQueue(null);
          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          setUser(false);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing.current = false;
        }
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      // Try refresh before giving up
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
